import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AddRoleDefaultRequest,
  AddRoleRequest,
  CreateRoleAttrubuteRequest,
  PermissionTypes,
  RoleAttribute,
  RoleAttributeListItem,
  RoleDefault,
  RoleInfo,
  UpdateRoleAttributeRequest,
  UpdateRoleRequest,
  UserRoleResponse,
} from '@models/authorization';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  findAndReplace,
  getAll,
  getOne,
  guidRegex,
  sortAlphabetically,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { RolesService } from './roles.service';

type RolesState = {
  roles: RoleInfo[];
  isLoading: boolean;
  isRoleCreateLoading: boolean;

  selectedRole: RoleInfo | null;
  isSelectedRoleLoading: boolean;

  permissions: PermissionTypes[];
  isPermissionsLoading: boolean;

  defaults: RoleDefault[];
  isDefaultsLoading: boolean;
  isDefaultCreateLoading: boolean;

  attributes: RoleAttributeListItem[];
  isAttributesLoading: boolean;
  isAttributeCreateLoading: boolean;

  selectedAttribute: RoleAttribute | null;
  isSelectedAttributeLoading: boolean;

  users: UserRoleResponse[];
  isUsersLoading: boolean;
};

const initialState: RolesState = {
  roles: [],
  isLoading: false,
  isRoleCreateLoading: false,

  selectedRole: null,
  isSelectedRoleLoading: false,

  permissions: [],
  isPermissionsLoading: false,

  defaults: [],
  isDefaultsLoading: false,
  isDefaultCreateLoading: false,

  attributes: [],
  isAttributesLoading: false,
  isAttributeCreateLoading: false,

  selectedAttribute: null,
  isSelectedAttributeLoading: false,

  users: [],
  isUsersLoading: false,
};

const rolesDictionary: StateDictionary<RoleInfo> = {
  entityList: 'roles',
  listLoading: 'isLoading',
  selectedEntity: 'selectedRole',
  selectedEntityLoading: 'isSelectedRoleLoading',
  entityId: 'id',
  sortFn: (a, b) => a.name.localeCompare(b.name),
  createLoading: 'isRoleCreateLoading',
};

export const RolesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('roles-store'),

  withComputed(store => ({
    selectedRoleId: computed(() => store.selectedRole()?.id || null),
    selectedAttributeId: computed(() => store.selectedAttribute()?.id || null),
  })),

  withMethods((store, rolesService = inject(RolesService), router = inject(Router)) => {
    createStateResetHandler<RolesState>('/roles')(store, initialState);
    createCustomStateSetOnUrlHandler<RolesState>('/roles', { selectedRole: null })(store);
    createCustomStateSetOnRegexHandler<RolesState>(new RegExp(`^/roles/(${guidRegex})/attributes$`))(store, {
      selectedAttribute: null,
    });

    const roleMethods = {
      getAll: getAll<RoleInfo>({
        store,
        service: rolesService,
        dictionary: rolesDictionary,
      }),

      getOne: getOne<RoleInfo>({
        store,
        service: rolesService,
        dictionary: rolesDictionary,
      }),

      addNewRole: addEntity<RoleInfo, AddRoleRequest>({
        store,
        service: rolesService,
        dictionary: rolesDictionary,
        onSuccessCallback: (newRole: RoleInfo) => {
          router.navigate(['roles', newRole.id, 'details']);
        },
      }),

      updateRole: updateEntity<RoleInfo, UpdateRoleRequest>({
        store,
        service: rolesService,
        dictionary: rolesDictionary,
      }),

      deleteSelectedRole: deleteEntity<RoleInfo>({
        store,
        service: rolesService,
        dictionary: rolesDictionary,
        onSuccessCallback: () => {
          router.navigate(['roles']);
          roleMethods.getAll();
        },
      }),
    };

    const permissionMethods = {
      getRolePermissions: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isPermissionsLoading: true })),
          switchMap(id =>
            rolesService.getRolePermissions(id).pipe(
              tap(permissions => {
                patchState(store, {
                  isPermissionsLoading: false,
                  permissions: sortAlphabetically<PermissionTypes>(permissions, (a, b) => a.localeCompare(b)),
                });
              }),
              catchError(() => {
                patchState(store, { isPermissionsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const defaultsMethods = {
      getRoleDefaults: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isDefaultsLoading: true })),
          switchMap(id =>
            rolesService.getRoleDefaults(id).pipe(
              tap(defaults => {
                patchState(store, {
                  isDefaultsLoading: false,
                  defaults,
                });
              }),
              catchError(() => {
                patchState(store, { isDefaultsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      addRoleDefault: rxMethod<AddRoleDefaultRequest>(
        pipe(
          tap(() =>
            patchState(store, {
              isDefaultsLoading: true,
              isDefaultCreateLoading: true,
            }),
          ),
          switchMap(body =>
            rolesService.addRoleDefault(body).pipe(
              tap(defaults => {
                patchState(store, {
                  isDefaultsLoading: false,
                  isDefaultCreateLoading: false,
                  defaults,
                });
              }),
              catchError(() => {
                patchState(store, {
                  isDefaultsLoading: false,
                  isDefaultCreateLoading: false,
                });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      deleteRoleDefault: rxMethod<{ roleDefaultId: string; roleId: string }>(
        pipe(
          tap(() => patchState(store, { isDefaultsLoading: true })),
          switchMap(({ roleDefaultId, roleId }) =>
            rolesService.deleteRoleDefault(roleDefaultId, roleId).pipe(
              tap(() => {
                patchState(store, {
                  isDefaultsLoading: false,
                  defaults: store.defaults().filter(d => d.id !== roleDefaultId),
                });
              }),
              catchError(() => {
                patchState(store, { isDefaultsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const attributesMethods = {
      getRoleAttributes: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isAttributesLoading: true })),
          switchMap(roleId =>
            rolesService.getRoleAttributes(roleId).pipe(
              tap(attributes => {
                patchState(store, {
                  isAttributesLoading: false,
                  attributes: sortAlphabetically<RoleAttributeListItem>(attributes, (a, b) => a.name.localeCompare(b.name)),
                });
              }),
              catchError(() => {
                patchState(store, { isAttributesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getOneRoleAttribute: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isSelectedAttributeLoading: true })),
          switchMap(id =>
            rolesService.getRoleAttribute(id).pipe(
              tap(attribute => {
                patchState(store, {
                  isSelectedAttributeLoading: false,
                  selectedAttribute: attribute,
                });
              }),
              catchError(() => {
                patchState(store, { isSelectedAttributeLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      addRoleAttribute: rxMethod<CreateRoleAttrubuteRequest>(
        pipe(
          tap(() =>
            patchState(store, {
              isAttributesLoading: true,
              isAttributeCreateLoading: true,
            }),
          ),
          switchMap(body =>
            rolesService.createRoleAttribute(body).pipe(
              tap(attribute => {
                patchState(store, {
                  isAttributesLoading: false,
                  isAttributeCreateLoading: false,
                  attributes: sortAlphabetically<RoleAttributeListItem>([...store.attributes(), attribute], (a, b) =>
                    a.name.localeCompare(b.name),
                  ),
                  selectedAttribute: attribute,
                });
                router.navigate(['roles', attribute.roleId, 'attributes', attribute.id, 'details']);
              }),
              catchError(() => {
                patchState(store, {
                  isAttributesLoading: false,
                  isAttributeCreateLoading: false,
                });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      updateRoleAttribute: rxMethod<{ attributeId: string; attribute: UpdateRoleAttributeRequest }>(
        pipe(
          tap(() => patchState(store, { isAttributesLoading: true, isSelectedAttributeLoading: true })),
          switchMap(({ attributeId, attribute }) =>
            rolesService.updateRoleAttribute(attributeId, attribute).pipe(
              tap(updatedAttribute => {
                patchState(store, {
                  isAttributesLoading: false,
                  isSelectedAttributeLoading: false,
                  selectedAttribute: updatedAttribute,
                  attributes: sortAlphabetically<RoleAttributeListItem>(
                    findAndReplace(store.attributes(), updatedAttribute, item => item.id === attributeId),
                    (a, b) => a.name.localeCompare(b.name),
                  ),
                });
              }),
              catchError(() => {
                patchState(store, { isAttributesLoading: false, isSelectedAttributeLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      deleteRoleAttribute: rxMethod<{ attributeId: string; roleId: string }>(
        pipe(
          tap(() => patchState(store, { isAttributesLoading: true })),
          switchMap(({ attributeId, roleId }) =>
            rolesService.deleteRoleAttribute(attributeId).pipe(
              tap(() => {
                patchState(store, {
                  isAttributesLoading: false,
                  selectedAttribute: null,
                });
                router.navigate(['roles', roleId, 'attributes']);
                attributesMethods.getRoleAttributes(roleId);
              }),
              catchError(() => {
                patchState(store, { isAttributesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const usersMethods = {
      getRoleUsers: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isUsersLoading: true })),
          switchMap(roleId =>
            rolesService.getRoleUsers(roleId).pipe(
              tap(users => {
                patchState(store, {
                  isUsersLoading: false,
                  users: sortAlphabetically<UserRoleResponse>(users, (a, b) => a.userEmail.localeCompare(b.userEmail)),
                });
              }),
              catchError(() => {
                patchState(store, { isUsersLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationActions = {
      goToRoleActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['roles', id]);
        if (!result) return false;

        patchState(store, { defaults: [], attributes: [], permissions: [], users: [] });
        if (!store.isSelectedRoleLoading()) roleMethods.getOne(id);
        return true;
      },

      goToRoleAttributeActions: async (attributeId: string): Promise<boolean> => {
        const selectedRole = store.selectedRole();
        if (!selectedRole) return false;

        const result = await router.navigate(['roles', selectedRole.id, 'attributes', attributeId]);
        if (!result) return false;

        if (!store.isSelectedAttributeLoading()) attributesMethods.getOneRoleAttribute(attributeId);
        return true;
      },
    };

    return {
      ...roleMethods,
      ...permissionMethods,
      ...defaultsMethods,
      ...attributesMethods,
      ...usersMethods,
      ...navigationActions,
    };
  }),
);

export interface RolesStoreType {
  readonly roles: Signal<RoleInfo[]>;
  readonly isLoading: Signal<boolean>;
  readonly isRoleCreateLoading: Signal<boolean>;
  readonly selectedRole: Signal<RoleInfo | null>;
  readonly isSelectedRoleLoading: Signal<boolean>;

  readonly permissions: Signal<PermissionTypes[]>;
  readonly isPermissionsLoading: Signal<boolean>;

  readonly defaults: Signal<RoleDefault[]>;
  readonly isDefaultsLoading: Signal<boolean>;
  readonly isDefaultCreateLoading: Signal<boolean>;

  readonly attributes: Signal<RoleAttributeListItem[]>;
  readonly isAttributesLoading: Signal<boolean>;
  readonly isAttributeCreateLoading: Signal<boolean>;

  readonly selectedAttribute: Signal<RoleAttribute | null>;
  readonly isSelectedAttributeLoading: Signal<boolean>;

  readonly users: Signal<UserRoleResponse[]>;
  readonly isUsersLoading: Signal<boolean>;

  readonly selectedRoleId: Signal<string | null>;
  readonly selectedAttributeId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly addNewRole: (dto: AddRoleRequest) => void;
  readonly updateRole: (params: { id: string; dto: UpdateRoleRequest }) => void;
  readonly deleteSelectedRole: (id: string) => void;

  readonly getRolePermissions: (id: string) => void;

  readonly getRoleDefaults: (id: string) => void;
  readonly addRoleDefault: (body: AddRoleDefaultRequest) => void;
  readonly deleteRoleDefault: (params: { roleDefaultId: string; roleId: string }) => void;

  readonly getRoleAttributes: (roleId: string) => void;
  readonly getOneRoleAttribute: (id: string) => void;
  readonly addRoleAttribute: (body: CreateRoleAttrubuteRequest) => void;
  readonly updateRoleAttribute: (params: { attributeId: string; attribute: UpdateRoleAttributeRequest }) => void;
  readonly deleteRoleAttribute: (params: { attributeId: string; roleId: string }) => void;

  readonly getRoleUsers: (roleId: string) => void;

  readonly goToRoleActions: (id: string) => Promise<boolean>;
  readonly goToRoleAttributeActions: (attributeId: string) => Promise<boolean>;
}
